import React, { useEffect, useState } from 'react';
import LeaderContext from './context/LeaderContext';
import { getCurrentSession, logout } from './api/moduleApi';
import { AUTH_EXPIRED_EVENT } from './api/client';
import LoginScreen from './components/auth/LoginScreen';
import MainLayout from './components/layout/MainLayout';
// Admin Pages
import AdminDashboard from './pages/Admin/AdminDashboard';
import TemplateManagement from './pages/Admin/TemplateManagement';
import AllDataReview from './pages/Admin/AllDataReview';
import AdminSettings from './pages/Admin/AdminSettings';
import AdminAccountSettings from './pages/Admin/AdminAccountSettings';
import AdminModuleManagement from './pages/Admin/AdminModuleManagement';
import PendingDeployments from './pages/Admin/PendingDeployments';
import CreateLeader from './pages/Admin/CreateLeader';
import DeployedSurveys from './pages/Admin/DeployedSurveys';
import Leaderboard from './pages/Admin/Leaderboard';
// Leader Pages
import LeaderDashboard from './pages/Leader/LeaderDashboard';
import ManageExtraQuestions from './pages/Leader/ManageExtraQuestions';
import DeploySurvey from './pages/Leader/DeploySurvey';
import LeaderSettings from './pages/Leader/LeaderSettings';
// Shared Pages
import ReviewResults from './pages/shared/ReviewResults';

// --- Main Application Component (Router) ---

const App = () => {
  const [userRole, setUserRole] = useState(null); // 'admin', 'leader', or null
  const [user, setUser] = useState(null);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [backendStatus, setBackendStatus] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const clearAuthState = () => {
    setUserRole(null);
    setUser(null);
    setSelectedLeader(null);
    setCurrentPage('dashboard');
  };

  
  // --- Auth Handlers ---
  const handleLogin = (userObj) => {
    // userObj should be { id, name, role, email }
    if (!userObj) return;
    setUser({ id: userObj.id, name: userObj.name, role: userObj.role, email: userObj.email });
    const roleKey = (userObj.role || '').toLowerCase().includes('admin') ? 'admin' : 'leader';
    setUserRole(roleKey);
    setCurrentPage('dashboard');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      clearAuthState();
    }
  };

  useEffect(() => {
    let active = true;

    const restoreSession = async () => {
      try {
        const sessionUser = await getCurrentSession();
        if (active && sessionUser) {
          handleLogin(sessionUser);
        }
      } catch (err) {
        console.error('Session restore failed:', err);
      } finally {
        if (active) {
          setAuthLoading(false);
        }
      }
    };

    const handleAuthExpired = () => {
      clearAuthState();
      setAuthLoading(false);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    restoreSession();

    return () => {
      active = false;
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, []);

  const navigate = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // --- Routing Logic ---
  const renderPageContent = () => {
    if (!userRole) return null; // Handled by MainLayout rendering LoginScreen

    if (userRole === 'admin') {
      switch (currentPage) {
        case 'dashboard': return <AdminDashboard user={user} />;
        case 'deployed-surveys': return <DeployedSurveys />;
        case 'pending-deploy': return <PendingDeployments user={user} />;
        case 'create-leader': return <CreateLeader />;
        case 'template': return <TemplateManagement />;
        case 'data': return <AllDataReview user={user} />;
        case 'leaderboard': return <Leaderboard />;
        case 'settings': return <AdminSettings />;
        case 'account': return <AdminAccountSettings user={user} onUserUpdated={setUser} />;
        case 'modules': return <AdminModuleManagement />;
        default: return <PendingDeployments user={user} />;
      }
    }

    if (userRole === 'leader') {
      switch (currentPage) {
        case 'dashboard': return <LeaderDashboard user={user} selectedLeader={selectedLeader} />;
        case 'settings': return <LeaderSettings user={user} selectedLeader={selectedLeader} />;
        case 'manage-q': return <ManageExtraQuestions user={user} selectedLeader={selectedLeader} />;
        case 'deploy': return <DeploySurvey user={user} selectedLeader={selectedLeader} />;
        case 'review': return <ReviewResults user={user} selectedLeader={selectedLeader} />;
        default: return <LeaderDashboard user={user} selectedLeader={selectedLeader} />;
      }
    }
  };

  // --- Main Render: Login Page vs. Dashboard ---
  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-indigo-600 font-semibold">Logging In...</div>;
  }

  return (
    <>
      {!userRole ? (
        // Renders the login screen
        <LoginScreen 
          handleLogin={handleLogin} 
          backendStatus={backendStatus} 
          setBackendStatus={setBackendStatus}
        />
      ) : (
        // Renders the dashboard
        <LeaderContext.Provider value={{ selectedLeader, setSelectedLeader }}>
          <MainLayout 
            user={user} 
            currentPage={currentPage} 
            navigate={navigate} 
            handleLogout={handleLogout}
            onLeaderSelect={setSelectedLeader}
            selectedLeader={selectedLeader}
          >
            {renderPageContent()}
          </MainLayout>
        </LeaderContext.Provider>
      )}
    </>
  );
};

export default App;